import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.js';
import { Button } from './ui/Button.js';
import { Card, CardBody, CardHeader, CardTitle } from './ui/Card.js';

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * AuthGuard wrapper — gating de routes basado en useAuth state.
 *
 * - loading: render spinner
 * - unauthenticated: <Navigate to="/login?return_to={path}" />
 * - error: render error page con retry button
 * - authenticated: render children
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const { state, refreshUser } = useAuth();
  const location = useLocation();

  if (state.status === 'loading') {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-distrito-200 border-t-distrito-600 rounded-full animate-spin" />
          <p className="text-sm text-gray-600">Verificando sesión…</p>
        </div>
      </div>
    );
  }

  if (state.status === 'unauthenticated') {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?return_to=${encodeURIComponent(returnTo)}`} replace />;
  }

  if (state.status === 'error') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Error de autenticación</CardTitle>
        </CardHeader>
        <CardBody>
          <p className="text-sm">{state.error}</p>
          <Button variant="primary" className="mt-4" onClick={() => void refreshUser()}>
            Reintentar
          </Button>
        </CardBody>
      </Card>
    );
  }

  return <>{children}</>;
}
